import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Navbar from 'src/components/NavBar';
import useAuth from 'src/hooks/useAuth';
import ComposeMessageFormFullScreen, {
  ComposeMessageFormFullScreenProps,
} from './ComposeMessageFormFullScreen';
import MessagePopUpContentWrapper from './ComposeMessagePopUpContentWrapper';

interface ComposeMessagePageProps extends ComposeMessageFormFullScreenProps {
  isFormValid: boolean;
  isSendDisabled: boolean;
  onSend: () => Promise<void>;
  onCancel: () => void;
  statusMessage?: string;
  showStatus?: boolean;
}

const ComposeMessagePage: FC<ComposeMessagePageProps> = ({
  isFormValid,
  isSendDisabled,
  onSend,
  onCancel,
  replyToMessage,
  statusMessage,
  showStatus,
  ...formProps
}) => {
  const { t } = useTranslation();
  const { clientId: authClientId, user } = useAuth();

  return (
    <>
      <Navbar title={replyToMessage ? t('Reply to Message') : t('Compose Message')} />

      <main className="message-layout main-container">
        {/* Status Message */}
        {showStatus && statusMessage && (
          <div className="mb-4">
            <div
              className={`text-sm font-medium p-3 rounded ${
                statusMessage.includes(t('Success'))
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {statusMessage}
            </div>
          </div>
        )}

        <div className="p-0 mb-6">
          {/* Buttons at top, centered */}
          <div className="flex justify-center items-center mb-6">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex flex-col items-center">
                <PopUp
                  show={isSendDisabled && !isFormValid}
                  className="z-50"
                  content={
                    <div className="mt-2">
                      <MessagePopUpContentWrapper
                        type="Error"
                        contents={[
                          ...(formProps.selectedRecipients?.length === 0 &&
                          (!formProps.formData?.recipient ||
                            formProps.formData?.recipient.trim().length === 0)
                            ? [t('Recipient is required.')]
                            : []),
                          ...(!formProps.formData?.subject ||
                          formProps.formData?.subject.trim().length === 0
                            ? [t('Subject is required.')]
                            : []),
                          ...(!formProps.formData?.message ||
                          formProps.formData?.message.trim().length === 0
                            ? [t('Message body is required.')]
                            : []),
                        ]}
                        prefix=""
                      />
                    </div>
                  }
                >
                  <Button color="primary" onClick={onSend} disabled={isSendDisabled}>
                    {t('Send Message')}
                  </Button>
                </PopUp>
              </div>
              <Button color="secondary" onClick={onCancel}>
                {t('Cancel')}
              </Button>
            </div>
          </div>
          {/* Form with full layout */}
          <div className="px-6">
            <ComposeMessageFormFullScreen
              replyToMessage={replyToMessage}
              clientId={authClientId}
              {...formProps}
            />
          </div>
        </div>
      </main>
    </>
  );
};

export default ComposeMessagePage;
